import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";

import ReferalSourcesAdd from "./ReferalSourcesAdd";
import ReferalSourcesEdit from "./ReferalSourcesEdit";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const ReferalSourcesList = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ---------------- Fetch ---------------- */
  const fetchReferralSources = async () => {
    try {
      const res = await ApiServices.get("/referral-sources/");
      setRows(res?.data || []);
    } catch {
      showSnack("Failed to load referral sources", "error");
    }
  };

  useEffect(() => {
    fetchReferralSources();
  }, []);

  /* ---------------- Snackbar ---------------- */
  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  /* ---------------- Toggle Refer Allow ---------------- */
  const toggleReferAllow = async (row) => {
    try {
      await ApiServices.patch(
        `referral-sources/${row.id}/toggle-ability/`
      );
      showSnack("Referral permission updated");
      fetchReferralSources();
    } catch {
      showSnack("Failed to update referral permission", "error");
    }
  };

  /* ---------------- Search ---------------- */
  const filteredRows = useMemo(() => {
    if (!search) return rows;
    return rows.filter((r) =>
      [r.name, r.email, r.phone, r.company]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [rows, search]);

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Referral Sources</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAdd(true)}
        >
          Add Referral Source
        </Button>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search referral sources"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Refer Allow</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell
                  sx={{ cursor: "pointer", color: "primary.main" }}
                  onClick={() =>
                    navigate(
                      `/admin-user/referal-source-details/${row.id}/${row.name}`
                    )
                  }
                >
                  {row.name}
                </TableCell>

                <TableCell>{row.email}</TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell>{row.company}</TableCell>

                {/* Toggle */}
                <TableCell>
                  <Switch
                    checked={row.refer_allow}
                    onChange={() => toggleReferAllow(row)}
                  />
                </TableCell>

                <TableCell>
                  <Chip
                    label={row.is_active ? "Active" : "Inactive"}
                    color={row.is_active ? "success" : "error"}
                    size="small"
                  />
                </TableCell>

                <TableCell align="center">
                  <IconButton
                    onClick={() => {
                      setSelectedRow(row);
                      setOpenEdit(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={openAdd} disableEscapeKeyDown fullWidth maxWidth="md">
        <DialogTitle>Add Referral Source</DialogTitle>
        <DialogContent>
          <ReferalSourcesAdd
            onClose={() => setOpenAdd(false)}
            onSuccess={(msg) => {
              showSnack(msg);
              setOpenAdd(false);
              fetchReferralSources();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEdit} disableEscapeKeyDown fullWidth maxWidth="md">
        <DialogTitle>Edit Referral Source</DialogTitle>
        <DialogContent>
          <ReferalSourcesEdit
            data={selectedRow}
            onClose={() => setOpenEdit(false)}
            onSuccess={(msg) => {
              showSnack(msg);
              setOpenEdit(false);
              fetchReferralSources();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ReferalSourcesList;
